import { DataTable } from "../../../../components/DataTable";
import { Box, Button, Flex, Select, Text, useToast } from "@chakra-ui/react";
import * as React from "react";
import {
  useExportYearByYearData,
  useGetSuppliersYearByYearAverageData,
  useGetSuppliersYearByYearData,
  useGetSuppliersYearlyData,
} from "../../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useCallback, useMemo, useState } from "react";
import {
  calculateDataTableState,
  constructQueryParamsString,
  getFormattedNumValue,
  getSortDirection,
  handlePageSizeChange,
  showErrorToast,
  translateSubElementString,
} from "../../../../utils";
import { MultiSelect } from "../../../../components";
import EmissionFilterComponent from "../EmissionFilterComponent";
import { getFilteredData } from "../../utils";

const DEFAULT_YEARS = ["2021", "2022", "2023", "2024"];

function EmissionsByYearTab(callback, deps) {
  const { t } = useTranslation("translation", {
    keyPrefix: "suppliersEmissionsDashboard.emissionsByYear",
  });
  const { t: globalTranslation } = useTranslation("translation");
  const dispatch = useDispatch();
  const toast = useToast();
  const [yearByYearEmissionsData, setYearByYearEmissionsData] = useState([]);
  const [years, setYears] = useState(DEFAULT_YEARS);
  const [comparisonYearStart, setComparisonYearStart] = useState(DEFAULT_YEARS[0]);
  const [comparisonYearEnd, setComparisonYearEnd] = useState(DEFAULT_YEARS[DEFAULT_YEARS.length - 1]);
  const [selectedDataQualityFilters, setSelectedDataQualityFilters] = useState([]);

  /**
   * Calculate growth for the given row between comparison years
   * Formula: ((endValue - startValue) / startValue) * 100
   * @param yearlyEmissonData
   */
  const calculateGrowth = useCallback(
    (yearlyEmissonData) => {
      const startYearValue = yearlyEmissonData?.[comparisonYearStart]?.totalCo2Emissions || 0;
      const endYearDataValue = yearlyEmissonData?.[comparisonYearEnd]?.totalCo2Emissions || 0;
      let growth = 0;
      if (startYearValue === 0) {
        return growth.toFixed(2);
      }
      growth = ((endYearDataValue - startYearValue) / startYearValue) * 100;
      return growth.toFixed(2);
    },
    [comparisonYearStart, comparisonYearEnd]
  );

  const dynamicEmissionsColumns = years.flatMap((year) => [
    {
      header: () => (
        <>
          {year} {translateSubElementString("suppliersEmissionsDashboard.emissionsByYear.gridHeaders.emissions")}
        </>
      ),
      accessorKey: `emissions-${year}`,
      size: 100,
      enableColumnFilter: false,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.yearlyEmissionData?.[year]?.totalCo2Emissions || 0;
        const b = rowB.original.yearlyEmissionData?.[year]?.totalCo2Emissions || 0;
        return a - b;
      },
      cell: ({ row }) => getFormattedNumValue(row.original.yearlyEmissionData?.[year]?.totalCo2Emissions || 0, 2),
    },
  ]);

  const columns = useMemo(() => {
    const dynamicGrowthColumns = [
      {
        header: () => (
          <Box>
            <Text>Growth Comparison</Text>
            <Flex direction="row" gap={2} mt={2}>
              <Select
                value={comparisonYearStart}
                onChange={(e) => {
                  setComparisonYearStart(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
              <Select
                value={comparisonYearEnd}
                onChange={(e) => {
                  setComparisonYearEnd(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
            </Flex>
          </Box>
        ),
        accessorKey: `emissions-growth`,
        size: 100,
        enableColumnFilter: false,
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const a = calculateGrowth(rowA.original?.yearlyEmissionData) || 0;
          const b = calculateGrowth(rowB.original?.yearlyEmissionData) || 0;
          return a - b;
        },
        cell: ({ row }) => {
          const growth = calculateGrowth(row.original?.yearlyEmissionData);
          return <Text color={growth < 0 ? "#1bd41b" : growth > 0 ? "red" : "black"}>{growth + " %"}</Text>;
        },
      },
    ];

    return [
      {
        header: t("gridHeaders.supplierName"),
        accessorKey: "supplierName",
        size: 120,
      },
      ...dynamicEmissionsColumns,
      ...dynamicGrowthColumns,
    ];
  }, [t, dynamicEmissionsColumns, comparisonYearStart, years, comparisonYearEnd, calculateGrowth]);

  const getQueryParamsString = () => {
    const queryParams = {
      years: years.length !== 0 ? years : DEFAULT_YEARS,
    };
    return constructQueryParamsString(queryParams);
  };

  // const { data: averageData } = useGetSuppliersYearByYearAverageData(filterObj, {
  //   onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  //   cacheTime: 0,
  // });

  const { isFetching } = useGetSuppliersYearlyData(getQueryParamsString(), {
    onSuccess: (data) => {
      // exclude data where excludeFromReport is true
      const filteredData = data.filter((item) => !item.excludeFromReport);
      setYearByYearEmissionsData(filteredData);
      // handlePageSizeChange(page, queryPageIndex, manageDataTableState);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  const filteredData = useMemo(() => {
    return getFilteredData(yearByYearEmissionsData, selectedDataQualityFilters);
  }, [yearByYearEmissionsData, selectedDataQualityFilters]);

  /**
   * Render the average growth label
   * Formula: (sum of growths) / (number of suppliers)
   * @returns {Element}
   */
  const renderAverageGrowthLabel = () => {
    const totalGrowth = filteredData.reduce((acc, row) => {
      const growth = parseFloat(calculateGrowth(row.yearlyEmissionData)) || 0;
      return acc + growth;
    }, 0);

    const averageGrowth = filteredData.length > 0 ? totalGrowth / filteredData.length : 0;

    const valueColor = averageGrowth < 0 ? "#1bd41b" : averageGrowth > 0 ? "red" : "black";
    const period = `${comparisonYearStart}-${comparisonYearEnd}`;
    return (
      <Text fontSize="xl" fontWeight="600">
        {t("averageGrowth", { period: period })}:{" "}
        <Text color={valueColor} as={"span"}>
          {averageGrowth.toFixed(2) + " %"}
        </Text>
      </Text>
    );
  };

  const { mutate: exportYearByYearData } = useExportYearByYearData({
    onMutate: () => dispatch(showAppSpinner()),
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const handleExport = () => {
    exportYearByYearData();
  };

  return (
    <Flex flexFlow={"column"} gap={4} marginBlock={4}>
      <Flex direction="column" gap={4} mt={8}>
        <Flex align="center" gap={4}>
          <Box as="label" fontWeight="bold" fontSize="sm" color="gray.600">
            Year:
          </Box>
          <Box minW="250px">
            <MultiSelect
              onChange={(value) => setYears(value.map((item) => item.value))}
              options={Array.from({ length: 10 }, (_, i) => {
                const year = new Date().getFullYear() + 1 - i;
                return { label: year.toString(), value: year.toString() };
              })}
              isMulti={true}
              defaultValue={years.map((year) => ({ label: year, value: year }))}
            />
          </Box>
        </Flex>
        <EmissionFilterComponent
          type="dataQuality"
          selectedValues={selectedDataQualityFilters}
          onChange={(e) => setSelectedDataQualityFilters(e)}
        />
      </Flex>
      <Flex justifyContent="space-between" alignItems="end">
        {/*{averageData !== null && typeof averageData !== "undefined" && (*/}
        <Box>
          {renderAverageGrowthLabel()}
          {/*{renderAverageGrowthLabel(averageData.secondToThirdAvgGrowth, "2022-2023")}*/}
          {/*{renderAverageGrowthLabel(averageData.firstToThirdAvgGrowth, "2021-2023")}*/}
        </Box>
        {/*)}*/}
        {/*<Button colorScheme="customScheme" onClick={handleExport}>*/}
        {/*  {globalTranslation("suppliersEmissionsDashboard.exportData")}*/}
        {/*</Button>*/}
      </Flex>
      <DataTable
        data={filteredData}
        columns={columns}
        isLoading={isFetching}
        serverSidePagination={false}
        enableSorting={true}
        enableFilters={true}
        downloadButtonText="Export Data"
      />
    </Flex>
  );
}

export default EmissionsByYearTab;
